/**
 * The boxes board's active-filter shape — shared by `boxes.tsx` (which owns the filter state and
 * applies it to the open list before grouping) and `box-filter-sheet.tsx` (which edits a draft of
 * it). Same split as `item-filters.ts`, for the same reason: the sheet imports the type without a
 * cycle.
 *
 * Every dimension is a SET, OR-combined inside a dimension and AND-combined across dimensions —
 * "boxes from ASOS or Zalando, overdue" is (source ∈ {ASOS, Zalando}) AND (deadline = overdue).
 * The null-holding values (no source picked, not placed, no deadline) are first-class options:
 * an unanswered question is filterable exactly because it is the thing someone chases.
 */

import { NO_KIND_LABEL, SOURCE_KIND_LABELS, SOURCE_KIND_VALUES } from '@mononoke/shared';
import type { BoxCard } from '../../../api/src/catalog/boxes.js';
import { directed, type SortDirection } from '../components/sort-direction.js';

/** Sentinel option values for the null cases — namespaced so a rack named "None" cannot collide.
 *  The not-placed sentinel is the board's own `NOT_PLACED` column key below, shared on purpose. */
export const FILTER_NO_SOURCE = '__no_source__';
export const FILTER_NO_KIND = '__no_kind__';

/* The board's place vocabulary — HOISTED from boxes.tsx (DRY, first duplicate) because the filter
 * sheet labels its Where pills with the same words the columns wear. The three built-ins are
 * SUGGESTIONS, never a constraint (the 044 lesson); the atelier's racks join them at runtime. */
export const BUILTIN_PLACES = ['receiving', 'packing bench', 'holding'];
export const PLACE_LABELS: Record<string, string> = {
  receiving: 'Receiving',
  'packing bench': 'Packing bench',
  holding: 'Holding',
};
/** The one-line explanation under each heading — frame 13c carries these on the columns. */
export const PLACE_HINTS: Record<string, string> = {
  receiving: 'Arrived, not yet worked through.',
  'packing bench': 'Being packed or unpacked right now.',
  holding: 'Waiting — for a courier, a decision, a date.',
};
export const NOT_PLACED = '__not_placed__';

export interface BoxFilters {
  /** Source names (`FILTER_NO_SOURCE` for boxes with no source picked yet). */
  sources?: string[] | undefined;
  /** Floor places (`NOT_PLACED` for boxes nobody has placed). */
  places?: string[] | undefined;
  /** Arrived / not yet (069) — the pre-arrival board, on its own axis. */
  arrival?: ('here' | 'expected')[] | undefined;
  /** Deadline buckets — the same predicates the deadline GROUPING draws columns for. */
  deadline?: ('overdue' | 'week' | 'later' | 'none')[] | undefined;
  /** Source kinds (`FILTER_NO_KIND` for a box whose source has no type yet). */
  kinds?: string[] | undefined;
  /** Project labels (`FILTER_NO_PROJECT` for a box filed under none) — 0.1.456, mirroring
   *  the Project group axis: a sheet that can GROUP by a thing can filter by it. */
  projects?: string[] | undefined;
}

export const FILTER_NO_PROJECT = '__no_project__';

export const DEADLINE_OPTIONS: { value: 'overdue' | 'week' | 'later' | 'none'; label: string }[] = [
  { value: 'overdue', label: 'Overdue' },
  { value: 'week', label: 'Due this week' },
  { value: 'later', label: 'Later' },
  { value: 'none', label: 'No deadline' },
];

function deadlineBucket(box: BoxCard): 'overdue' | 'week' | 'later' | 'none' {
  if (box.daysLeft === null) return 'none';
  if (box.daysLeft < 0) return 'overdue';
  if (box.daysLeft <= 7) return 'week';
  return 'later';
}

/** The sort axes; each is sorted here since 0.1.393, so the direction toggle governs all of them. */
export type BoxSort = 'deadline' | 'received' | 'expected' | 'name';

export function matchesBoxFilters(box: BoxCard, filters: BoxFilters): boolean {
  if (filters.sources !== undefined && filters.sources.length > 0) {
    const key = box.sourceName ?? FILTER_NO_SOURCE;
    if (!filters.sources.includes(key)) return false;
  }
  if (filters.places !== undefined && filters.places.length > 0) {
    const key = box.floorLocation ?? NOT_PLACED;
    if (!filters.places.includes(key)) return false;
  }
  if (filters.deadline !== undefined && filters.deadline.length > 0) {
    if (!filters.deadline.includes(deadlineBucket(box))) return false;
  }
  if (filters.arrival !== undefined && filters.arrival.length > 0) {
    if (!filters.arrival.includes(box.receivedOn === null ? 'expected' : 'here')) return false;
  }
  if (filters.kinds !== undefined && filters.kinds.length > 0) {
    const key = box.sourceKind ?? FILTER_NO_KIND;
    if (!filters.kinds.includes(key)) return false;
  }
  if (filters.projects !== undefined && filters.projects.length > 0) {
    const keys = box.projects.length === 0 ? [FILTER_NO_PROJECT] : box.projects;
    if (!keys.some((k) => (filters.projects ?? []).includes(k))) return false;
  }
  return true;
}

/** Dimensions touched, not pills lit — the same convention as the items list's `countFilters`. */
export function countBoxFilters(filters: BoxFilters): number {
  return (
    (filters.sources !== undefined && filters.sources.length > 0 ? 1 : 0) +
    (filters.places !== undefined && filters.places.length > 0 ? 1 : 0) +
    (filters.deadline !== undefined && filters.deadline.length > 0 ? 1 : 0) +
    (filters.arrival !== undefined && filters.arrival.length > 0 ? 1 : 0) +
    (filters.kinds !== undefined && filters.kinds.length > 0 ? 1 : 0) +
    (filters.projects !== undefined && filters.projects.length > 0 ? 1 : 0)
  );
}

/**
 * ⚠️ THE DIRECTION APPLIES TO EVERY AXIS, `deadline` INCLUDED (0.1.393). That one used to return the
 * server's order untouched, so the new ascending/descending toggle would have silently done nothing
 * on the board's DEFAULT sort — the one people press it on first. It is sorted here now, ascending
 * meaning soonest-due, which is the order the server already sent.
 *
 * ⚠️ A MISSING DATE SORTS LAST IN BOTH DIRECTIONS. The null test comes before the comparison and is
 * NOT flipped: an undated parcel has no position on a date axis, and promoting the ones nobody has
 * dated to the top when the list is reversed would read as "these are the newest". Mirrors the
 * server's own `NULLS LAST`. Each axis ends in the title, so the order is total and two boxes
 * sharing a date cannot swap places between renders.
 */
export function sortBoxes(boxes: BoxCard[], sort: BoxSort, direction: SortDirection = 'asc'): BoxCard[] {
  const copy = [...boxes];
  const byDate = (a: string | null, b: string | null): number => {
    if (a === b) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return directed(a.localeCompare(b), direction);
  };
  const byTitle = (a: BoxCard, b: BoxCard): number => directed(a.title.localeCompare(b.title), direction);
  copy.sort((a, b) => {
    if (sort === 'received') return byDate(a.receivedOn, b.receivedOn) || byTitle(a, b);
    if (sort === 'expected') return byDate(a.expectedOn, b.expectedOn) || byTitle(a, b);
    if (sort === 'deadline') return byDate(a.returnDueOn, b.returnDueOn) || byTitle(a, b);
    return byTitle(a, b);
  });
  return copy;
}

/** The dimensions' words — the chips, the picker rows and the hints all read from here. */
export const BOX_DIMENSION_LABELS = {
  sources: 'Source',
  kinds: 'Source type',
  places: 'Location',
  deadline: 'Deadline',
  arrival: 'Arrival',
  projects: 'Project',
} as const;

function kindLabel(v: string): string {
  return v === FILTER_NO_KIND ? NO_KIND_LABEL : ((SOURCE_KIND_LABELS as Record<string, string | undefined>)[v] ?? v);
}

/**
 * THE ACTIVE FILTERS AS CHIPS (phone display P2, frame 57's Filters row; the desk bar and
 * panel since 0.1.456): one chip per lit value, its dimension's word in front, each removable
 * on its own. Keys are `dimension:value` so `removeBoxFilter` can take exactly one back out.
 */
export function describeBoxFilters(filters: BoxFilters): { key: string; dim: string; label: string }[] {
  const chips: { key: string; dim: string; label: string }[] = [];
  const L = BOX_DIMENSION_LABELS;
  for (const v of filters.sources ?? []) chips.push({ key: `sources:${v}`, dim: L.sources, label: v === FILTER_NO_SOURCE ? 'No source yet' : v });
  for (const v of filters.kinds ?? []) chips.push({ key: `kinds:${v}`, dim: L.kinds, label: kindLabel(v) });
  for (const v of filters.places ?? []) chips.push({ key: `places:${v}`, dim: L.places, label: v === NOT_PLACED ? 'Not placed yet' : (PLACE_LABELS[v] ?? v) });
  for (const v of filters.deadline ?? []) chips.push({ key: `deadline:${v}`, dim: L.deadline, label: DEADLINE_OPTIONS.find((d) => d.value === v)?.label ?? v });
  for (const v of filters.arrival ?? []) chips.push({ key: `arrival:${v}`, dim: L.arrival, label: v === 'here' ? 'Here' : 'Not here yet' });
  for (const v of filters.projects ?? []) chips.push({ key: `projects:${v}`, dim: L.projects, label: v === FILTER_NO_PROJECT ? 'No project' : v });
  return chips;
}

/** Flip one value in one dimension — the picker's `toggle`, as a pure step. */
export function toggleBoxFilter(filters: BoxFilters, dim: keyof BoxFilters, value: string): BoxFilters {
  const current: string[] = filters[dim] ?? [];
  const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
  return { ...filters, [dim]: next.length > 0 ? next : undefined };
}

/**
 * THE PICKER'S DIMENSIONS (0.1.456): what the boxes list can be narrowed by, in plain words,
 * each with the one-line hint the maintainer asked for ("a lot of options that I don't know
 * what to do with"). Options are what is actually on the board (the old sheet's rule — six
 * type pills where two apply is furniture), the null case first because an unanswered
 * question is exactly what somebody opens a filter to chase. Arrival is offered only while
 * an expected parcel exists (069's rule, kept).
 */
export function boxFilterDimensions(
  boxes: BoxCard[],
  places: string[],
  filters: BoxFilters,
  set: (next: BoxFilters) => void,
): {
  id: string;
  label: string;
  hint: string;
  options: { value: string; label: string }[];
  selected: string[];
  multi: boolean;
  toggle: (value: string) => void;
}[] {
  const L = BOX_DIMENSION_LABELS;
  const sourceNames = [...new Set(boxes.map((b) => b.sourceName).filter((n): n is string => n !== null))].sort((a, b) => a.localeCompare(b));
  const placeKeys = [...places];
  for (const b of boxes) if (b.floorLocation !== null && !placeKeys.includes(b.floorLocation)) placeKeys.push(b.floorLocation);
  const kindsPresent = SOURCE_KIND_VALUES.filter((k) => boxes.some((b) => b.sourceKind === k));
  const projectNames = [...new Set(boxes.flatMap((b) => b.projects))].sort((a, b) => a.localeCompare(b));
  const dim = (
    id: keyof BoxFilters,
    hint: string,
    options: { value: string; label: string }[],
  ): ReturnType<typeof boxFilterDimensions>[number] => ({
    id,
    label: L[id],
    hint,
    options,
    selected: filters[id] ?? [],
    multi: true,
    toggle: (value) => set(toggleBoxFilter(filters, id, value)),
  });
  const out = [
    dim('sources', 'Who the parcel came from', [
      ...(boxes.some((b) => b.sourceName === null) ? [{ value: FILTER_NO_SOURCE, label: 'No source yet' }] : []),
      ...sourceNames.map((n) => ({ value: n, label: n })),
    ]),
    dim('kinds', 'Lender, shop, designer…', [
      ...(boxes.some((b) => b.sourceKind === null) ? [{ value: FILTER_NO_KIND, label: NO_KIND_LABEL }] : []),
      ...kindsPresent.map((k) => ({ value: k, label: SOURCE_KIND_LABELS[k] })),
    ]),
    dim('places', 'Where it stands in the atelier', [
      ...(boxes.some((b) => b.floorLocation === null) ? [{ value: NOT_PLACED, label: 'Not placed yet' }] : []),
      ...placeKeys.map((p) => ({ value: p, label: PLACE_LABELS[p] ?? p })),
    ]),
    dim('deadline', 'When it has to go back', DEADLINE_OPTIONS.map((d) => ({ value: d.value, label: d.label }))),
    ...(boxes.some((b) => b.receivedOn === null)
      ? [
          dim('arrival', 'Here, or still expected', [
            { value: 'here', label: 'Here' },
            { value: 'expected', label: 'Not here yet' },
          ]),
        ]
      : []),
    dim('projects', 'The production it is filed under', [
      ...(boxes.some((b) => b.projects.length === 0) ? [{ value: FILTER_NO_PROJECT, label: 'No project' }] : []),
      ...projectNames.map((n) => ({ value: n, label: n })),
    ]),
  ];
  return out;
}

export function removeBoxFilter(filters: BoxFilters, key: string): BoxFilters {
  const at = key.indexOf(':');
  const dim = key.slice(0, at) as keyof BoxFilters;
  const value = key.slice(at + 1);
  const current: string[] = filters[dim] ?? [];
  const next = current.filter((v) => v !== value);
  return { ...filters, [dim]: next.length > 0 ? next : undefined };
}

/**
 * A STORED Boxes filter set (0.1.522 — filters persist, display/use-stored-filters.ts), made safe
 * to trust: each dimension kept only as a list of strings, the two closed vocabularies (arrival,
 * deadline) only with values this build still offers. Never throws.
 */
export function sanitizeBoxFilters(raw: unknown): BoxFilters | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const list = (v: unknown): string[] | undefined => {
    if (!Array.isArray(v)) return undefined;
    const out = v.filter((x): x is string => typeof x === 'string');
    return out.length > 0 ? out : undefined;
  };
  const out: BoxFilters = {};
  for (const key of ['sources', 'places', 'kinds', 'projects'] as const) {
    const v = list(r[key]);
    if (v !== undefined) out[key] = v;
  }
  const arrival = list(r.arrival)?.filter((v): v is 'here' | 'expected' => v === 'here' || v === 'expected');
  if (arrival !== undefined && arrival.length > 0) out.arrival = arrival;
  const deadline = list(r.deadline)?.filter((v): v is 'overdue' | 'week' | 'later' | 'none' =>
    DEADLINE_OPTIONS.some((o) => o.value === v),
  );
  if (deadline !== undefined && deadline.length > 0) out.deadline = deadline;
  return out;
}
